// /* GS://PROTOCOLO
//  * Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web\src\components\ui\MoneyInput.jsx
//  * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
//  * Versão: 1.0.0 – Componente padrão de campo monetário centesimal.
//  * Impacto da Versão: [src/components/ui/MoneyInput.jsx]
//  * Data: 22/11/2025
//  * Responsável: ChatGPT (conforme Protocolo)
//  *
//  * Mudanças desta versão:
//  * - CRIADO: Componente MoneyInput com digitação centesimal (modo caixa registradora).
//  * - DEFINIDO: Padrão único de entrada para valores monetários em todo o Ecossistema GoldenSisters.
//  * - SUPORTE: Uso controlado/semicontrolado via props valueInCents e onValueChange.
//  */

import React, { useEffect, useState, useCallback } from 'react';

/**
 * MoneyInput
 *
 * Campo de dinheiro em modo centesimal:
 * - Digita "1" → R$ 0,01
 * - Digita "2" → R$ 0,12
 * - Digita "3" → R$ 1,23
 * - Backspace remove o último dígito de centavos.
 *
 * VALOR INTERNO: sempre em centavos (inteiro).
 *
 * Props principais:
 * - valueInCents?: number      → valor controlado, em centavos.
 * - defaultValueInCents?: number → valor inicial, em centavos (modo semiconsciente).
 * - onValueChange?: (cents: number) => void
 * - label?: string
 * - id?: string
 * - name?: string
 * - disabled?: boolean
 * - required?: boolean
 * - maxCents?: number          → valor máximo permitido em centavos.
 * - className?: string         → classes extras no input.
 * - error?: string             → mensagem de erro opcional.
 */
const MoneyInput = ({
  valueInCents,
  defaultValueInCents = 0,
  onValueChange,
  label,
  id,
  name,
  disabled = false,
  required = false,
  maxCents = 99999999, // R$ 999.999,99
  className = '',
  error,
  onBlur,
  onFocus,
}) => {
  // Armazenamos apenas dígitos (string), representando centavos.
  const initialCents =
    typeof valueInCents === 'number'
      ? Math.max(0, Math.min(maxCents, Math.round(valueInCents)))
      : Math.max(0, Math.min(maxCents, Math.round(defaultValueInCents || 0)));

  const initialDigits = centsToDigits(initialCents);

  const [digits, setDigits] = useState(initialDigits);

  // Efeito para sincronizar quando o componente for controlado externamente.
  useEffect(() => {
    if (typeof valueInCents === 'number') {
      const normalized = Math.max(0, Math.min(maxCents, Math.round(valueInCents)));
      setDigits(centsToDigits(normalized));
    }
  }, [valueInCents, maxCents]);

  const notifyChange = useCallback(
    (newDigits) => {
      const cents = digitsToCents(newDigits);
      if (onValueChange) {
        onValueChange(cents);
      }
    },
    [onValueChange]
  );

  const handleKeyDown = (event) => {
    if (disabled) return;

    const { key } = event;

    // Permitir teclas de navegação sem interferir
    const allowedNavigationKeys = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
      'Shift',
      'Control',
      'Alt',
      'Meta',
    ];

    if (allowedNavigationKeys.includes(key)) {
      return; // não bloqueia
    }

    // Enter: deixamos passar (o formulário decide o que fazer)
    if (key === 'Enter') {
      return;
    }

    // Backspace: remove último dígito
    if (key === 'Backspace') {
      event.preventDefault();
      const newDigits = removeLastDigit(digits);
      setDigits(newDigits);
      notifyChange(newDigits);
      return;
    }

    // Delete: tratamos como limpar tudo
    if (key === 'Delete') {
      event.preventDefault();
      const newDigits = '0';
      setDigits(newDigits);
      notifyChange(newDigits);
      return;
    }

    // Números 0-9
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      const currentCents = digitsToCents(digits);
      const currentDigits = centsToDigits(currentCents);

      // concatena o novo dígito ao fim
      let nextDigits = currentDigits === '0' ? key : currentDigits + key;

      // aplica limite máximo em centavos
      const nextCents = digitsToCents(nextDigits);
      if (nextCents > maxCents) {
        return; // ignora se passar do limite
      }

      nextDigits = centsToDigits(nextCents);
      setDigits(nextDigits);
      notifyChange(nextDigits);
      return;
    }

    // Bloqueia qualquer outro caractere (vírgula, ponto, letras, etc.)
    event.preventDefault();
  };

  const handlePaste = (event) => {
    if (disabled) return;

    event.preventDefault();
    const text = event.clipboardData?.getData('text') || '';
    const onlyDigits = (text.match(/\d+/g) || []).join('');
    if (!onlyDigits) return;

    // Interpreta o valor colado como centavos (ex.: "12345" → 123,45)
    const rawDigits = removeLeadingZeros(onlyDigits) || '0';
    const cents = digitsToCents(rawDigits);
    const bounded = Math.max(0, Math.min(maxCents, cents));
    const nextDigits = centsToDigits(bounded);

    setDigits(nextDigits);
    notifyChange(nextDigits);
  };

  const handleChange = (event) => {
    // O valor visual é sempre derivado de "digits".
    // Não usamos o onChange do input para edição livre.
    // Mantemos apenas para evitar warning de React.
    event.preventDefault();
  };

  const valueToDisplay = formatCentsToCurrency(digitsToCents(digits));

  const inputId = id || name || undefined;

  return (
    <div className="flex flex-col space-y-1">
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-gray-300"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">
          R$
        </span>
        <input
          id={inputId}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          required={required}
          value={valueToDisplay.replace(/^R\$\s?/, '')}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          className={
            'w-full pl-9 pr-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-sm text-white ' +
            'focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold ' +
            (disabled ? 'opacity-60 cursor-not-allowed ' : '') +
            (error ? 'border-red-500 ' : '') +
            className
          }
        />
      </div>

      {error && (
        <p className="text-xs text-red-400 mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Converte centavos (inteiro) para string de dígitos.
 * Ex.: 0 → '0', 123 → '123'
 */
function centsToDigits(cents) {
  const safe = Number.isFinite(cents) ? Math.max(0, Math.floor(cents)) : 0;
  return removeLeadingZeros(String(safe)) || '0';
}

/**
 * Converte string de dígitos para centavos.
 * Ex.: '0' → 0, '1' → 1, '123' → 123 (R$ 1,23).
 */
function digitsToCents(digits) {
  if (!digits) return 0;
  const onlyDigits = digits.replace(/\D/g, '');
  if (!onlyDigits) return 0;
  const n = parseInt(onlyDigits, 10);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Remove zeros à esquerda, mantendo pelo menos um dígito.
 */
function removeLeadingZeros(s) {
  if (!s) return '0';
  const trimmed = s.replace(/^0+/, '');
  return trimmed === '' ? '0' : trimmed;
}

/**
 * Remove o último dígito; se só houver um, volta para '0'.
 */
function removeLastDigit(digits) {
  if (!digits || digits.length <= 1) return '0';
  const sliced = digits.slice(0, -1);
  return removeLeadingZeros(sliced);
}

/**
 * Formata centavos em moeda brasileira (R$).
 */
function formatCentsToCurrency(cents) {
  const safe = Number.isFinite(cents) ? cents : 0;
  const value = safe / 100;
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Fallback simples, caso Intl falhe por algum motivo.
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  }
}

export default MoneyInput;
